import { Link, useParams } from "@tanstack/react-router";
import { AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";

import {
  AccountingTransactionsReadyToExportBadge,
  AccountingTransactionsTodosBadge,
} from "@/components/shared/app-navigation/sidebar/badges/company";
import AIProcessingCard from "@/components/shared/cards/ai-processing-card";
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
} from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";

import { useMemo } from "react";
import { BankBookingsExportTable } from "./bank-bookings-export-table";
import { ExportFilterBadge } from "./export-filter-badge";
import { BankBookingsExportPanel } from "./export-panel/bank-bookings-export-panel";
import { useBankBookingsTable } from "./use-bank-bookings-table";

export default function LedgerEntriesTable() {
  const { t } = useTranslation();
  const { company: companyId } = useParams({
    from: "/_authenticated/$company",
  });

  const table = useBankBookingsTable({
    tableName: "bank-bookings-export",
    queryKeyPrefix: "bank-bookings-export",
  });

  const selectedBankTransactionIds = useMemo(() => {
    return table.table
      .getFilteredSelectedRowModel()
      .rows.map((row) => row.original.documentReference);
  }, [table.table, table.table.getFilteredSelectedRowModel().rows]);

  const handleOpenExport = (exportId: string) => {
    table.setFilters({
      datevExportId: exportId,
    });
  };

  return (
    <div className="h-full flex">
      <div className="flex flex-1">
        <div className="flex-1 flex flex-col min-w-0">
          <div className="flex px-4 bg-background pb-2">
            <AIProcessingCard screen="txn-todos" className="mt-4" />
          </div>
          <div className="flex-1 flex">
            <div className="flex flex-col min-w-0 w-full relative">
              <div>
                <NavigationMenu className="px-4 bg-background max-w-full justify-start border-b">
                  <NavigationMenuList className="space-x-0">
                    <NavigationMenuItem>
                      <Link
                        to="/$company/buchhaltung/transaktionen/todos"
                        params={{ company: companyId }}
                      >
                        <NavigationMenuLink
                          active
                          className={cn(
                            "text-gray-500 flex items-center py-3 px-6 gap-2 font-medium text-lg rounded-none",
                            "inline-flex justify-center whitespace-nowrap ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
                          )}
                        >
                          {t("transactions.tabs.todos")}
                          <AccountingTransactionsTodosBadge />
                        </NavigationMenuLink>
                      </Link>
                    </NavigationMenuItem>
                    <NavigationMenuItem>
                      <Link
                        to="/$company/buchhaltung/transaktionen/export"
                        params={{ company: companyId }}
                      >
                        <NavigationMenuLink
                          active
                          className={cn(
                            "text-gray-500 flex items-center py-3 px-6 gap-4 font-medium text-lg",
                            "inline-flex justify-center whitespace-nowrap ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
                            "border-b-[3px] text-primary border-primary cursor-default",
                          )}
                          onClick={(e) => e.preventDefault()}
                        >
                          {t("transactions.tabs.export")}
                          <AccountingTransactionsReadyToExportBadge />
                        </NavigationMenuLink>
                      </Link>
                    </NavigationMenuItem>
                    <NavigationMenuItem>
                      <Link
                        to="/$company/buchhaltung/transaktionen"
                        params={{ company: companyId }}
                      >
                        <NavigationMenuLink
                          className={cn(
                            "text-gray-500 flex items-center py-3 px-6 gap-2 font-medium text-lg rounded-none",
                            "inline-flex justify-center whitespace-nowrap ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
                          )}
                        >
                          {t("transactions.tabs.all_transactions")}
                        </NavigationMenuLink>
                      </Link>
                    </NavigationMenuItem>
                  </NavigationMenuList>
                </NavigationMenu>
              </div>
              <div className="flex-1 flex flex-col">
                <div className="flex-1 p-4 flex flex-col gap-3">
                  <ExportFilterBadge
                    exportId={table.filters.datevExportId as string | undefined}
                    onClear={() =>
                      table.setFilters({ datevExportId: undefined })
                    }
                  />
                  <BankBookingsExportTable {...table} />
                </div>
              </div>
            </div>
          </div>
        </div>
        <AnimatePresence mode="wait">
          <BankBookingsExportPanel
            bankTransactionIds={selectedBankTransactionIds}
            resetRowSelection={table.table.resetRowSelection}
            totalBankTransactions={table.pagination.totalItems}
            onOpenExport={handleOpenExport}
            selectedExportId={table.filters.datevExportId as string | undefined}
          />
        </AnimatePresence>
      </div>
    </div>
  );
}
